/**
 * PourAttachmentsSection — list, upload, download and delete files attached
 * to a pour. Files are stored in Azure Blob Storage under per-company folders
 * (or local disk in dev). The download button opens a SAS-signed URL.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { poursAPI, type PourAttachment } from '../../services/pours';
import { apiErrorText } from '../../utils/apiError';
import ImagePreviewDialog from './ImagePreviewDialog';
import { BRAND_GREEN } from './pourWizardStyles';

interface PourAttachmentsSectionProps {
  pourId: string;
  mode: 'create' | 'edit' | 'view';
  /** 'dropzone' renders the create wizard's dashed click-to-add zone so the
   *  edit page matches PourCreate; 'button' keeps the original compact
   *  Upload File button (PourSetupLegacy). */
  variant?: 'button' | 'dropzone';
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const isImageFile = (fileName: string) =>
  /\.(jpe?g|png|gif|webp|bmp)$/i.test(fileName);

const PourAttachmentsSection: React.FC<PourAttachmentsSectionProps> = ({ pourId, mode, variant = 'button' }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [attachments, setAttachments] = useState<PourAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ src: string; name: string } | null>(null);
  const isReadOnly = mode === 'view';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await poursAPI.listPourAttachments(pourId);
      setAttachments(data);
    } catch (err: any) {
      setError(apiErrorText(err, 'Failed to load attachments'));
    } finally {
      setLoading(false);
    }
  }, [pourId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSelectFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      setError(t('pourAttachments.tooLarge', 'File too large (max 25 MB)'));
      return;
    }

    setUploading(true);
    setError(null);
    try {
      await poursAPI.uploadPourAttachment(pourId, file);
      await refresh();
    } catch (err: any) {
      setError(apiErrorText(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (attachment: PourAttachment) => {
    if (attachment.download_url) {
      window.open(attachment.download_url, '_blank');
    } else {
      setError('Download URL not available');
    }
  };

  const handleDelete = async (attachment: PourAttachment) => {
    if (!window.confirm(t('pourAttachments.confirmDelete', 'Delete this attachment?'))) {
      return;
    }
    setError(null);
    try {
      await poursAPI.deletePourAttachment(pourId, attachment.file_id);
      setAttachments((prev) => prev.filter((a) => a.file_id !== attachment.file_id));
    } catch (err: any) {
      setError(apiErrorText(err, 'Delete failed'));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!isReadOnly && variant === 'dropzone' && (
        <Paper
          onClick={uploading ? undefined : handleSelectFile}
          elevation={0}
          sx={{
            p: 4,
            border: '2px dashed',
            borderColor: attachments.length > 0 ? BRAND_GREEN : 'grey.300',
            backgroundColor: 'grey.50',
            textAlign: 'center',
            minHeight: 160,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploading ? 'default' : 'pointer',
            borderRadius: 2,
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: BRAND_GREEN },
          }}
        >
          {uploading ? (
            <CircularProgress size={40} sx={{ mb: 1 }} />
          ) : (
            <AddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          )}
          <Typography variant="body2" color="text.secondary">
            {uploading
              ? t('pourAttachments.uploading', 'Uploading...')
              : t('pourAttachments.dropzoneHint', 'Click to add attachments (images, PDF, Word, Excel)')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('pourAttachments.dropzoneMax', 'Max 25MB per file')}
          </Typography>
        </Paper>
      )}
      {!isReadOnly && variant === 'button' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={18} /> : <UploadFileIcon />}
            onClick={handleSelectFile}
            disabled={uploading}
          >
            {uploading
              ? t('pourAttachments.uploading', 'Uploading...')
              : t('pourAttachments.uploadFile', 'Upload File')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('pourAttachments.allowedHint', 'PDF, Office docs, images. Max 25 MB.')}
          </Typography>
        </Box>
      )}
      {!isReadOnly && (
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
          onChange={handleFileChange}
        />
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress />}

      {!loading && attachments.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('pourAttachments.empty', 'No attachments uploaded yet.')}
        </Typography>
      )}

      {attachments.length > 0 && (
        <List dense disablePadding>
          {attachments.map((attachment) => (
            <ListItem
              key={attachment.file_id}
              divider
              sx={{ pr: isReadOnly ? 6 : 12 }}
            >
              {isImageFile(attachment.file_name) && attachment.download_url && (
                <Box
                  component="img"
                  src={attachment.download_url}
                  alt={attachment.file_name}
                  loading="lazy"
                  onClick={() =>
                    setPreview({ src: attachment.download_url as string, name: attachment.file_name })
                  }
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 1,
                    mr: 1.5,
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 },
                  }}
                />
              )}
              <ListItemText
                primary={attachment.file_name}
                secondary={attachment.file_path}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true, sx: { fontSize: '0.75rem' } }}
              />
              <ListItemSecondaryAction>
                <Tooltip title={t('pourAttachments.download', 'Download')}>
                  <IconButton edge="end" onClick={() => handleDownload(attachment)}>
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                {!isReadOnly && (
                  <Tooltip title={t('pourAttachments.delete', 'Delete')}>
                    <IconButton edge="end" onClick={() => handleDelete(attachment)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <ImagePreviewDialog
        src={preview?.src ?? null}
        name={preview?.name}
        onClose={() => setPreview(null)}
      />
    </Box>
  );
};

export default PourAttachmentsSection;
